export const SUPPORTER_MIN_ENTITLEMENT = 300;
export const SUPPORTER_ANIMATION_ENTITLEMENT = 600;

export const DEFAULT_SUPPORTER_COLOR = '';

export const APPROVED_SUPPORTER_COLORS = [
  // Original colors — unchanged
  {
    id: 'red',
    label: 'Red',
    value: '#c33d3d',
  },
  {
    id: 'pink',
    label: 'Pink',
    value: '#c33da3',
  },
  {
    id: 'violet',
    label: 'Violet',
    value: '#81368f',
  },
  {
    id: 'blue',
    label: 'Blue',
    value: '#36558f',
  },
  {
    id: 'teal',
    label: 'Teal',
    value: '#36838f',
  },
  {
    id: 'green',
    label: 'Green',
    value: '#368f55',
  },
  {
    id: 'orchid',
    label: 'Orchid',
    value: '#99508c',
  },
  {
    id: 'orange',
    label: 'Orange',
    value: '#c96f00',
  },

  // Additional distinct colors
  {
    id: 'gold',
    label: 'Gold',
    value: '#d4a72c',
  },
  {
    id: 'sky',
    label: 'Sky',
    value: '#2f8fd5',
  },
  {
    id: 'mint',
    label: 'Mint',
    value: '#3fbf8f',
  },
  {
    id: 'coral',
    label: 'Coral',
    value: '#dc6b5f',
  },
];

export const SUPPORTER_GRADIENT_DIRECTIONS = [
  {
    value: '90',
    label: 'Horizontal',
  },
  {
    value: '120',
    label: 'Diagonal',
  },
  {
    value: '180',
    label: 'Vertical',
  },
  {
    value: '225',
    label: 'Reverse diagonal',
  },
];

/*
 * Every preset is animated.
 * This entire list is only shown to 600+ users.
 */
export const SUPPORTER_GRADIENT_PRESETS = [
  {
    id: 'preset-sunset',
    label: 'Sunset',
    colorIds: ['red', 'orange', 'pink', 'red'],
    direction: 120,
    animated: true,
  },
  {
    id: 'preset-arcane',
    label: 'Arcane',
    colorIds: ['blue', 'violet', 'pink', 'blue'],
    direction: 120,
    animated: true,
  },
  {
    id: 'preset-tide',
    label: 'Tide',
    colorIds: ['blue', 'teal', 'green', 'blue'],
    direction: 90,
    animated: true,
  },
  {
    id: 'preset-hearth',
    label: 'Hearth',
    colorIds: ['red', 'orange', 'orchid', 'red'],
    direction: 225,
    animated: true,
  },
  {
    id: 'preset-aurora',
    label: 'Aurora',
    colorIds: ['sky', 'mint', 'violet', 'sky'],
    direction: 120,
    animated: true,
  },
  {
    id: 'preset-golden-hour',
    label: 'Golden Hour',
    colorIds: ['coral', 'gold', 'orange', 'coral'],
    direction: 90,
    animated: true,
  },
];

const APPROVED_COLOR_BY_ID = Object.fromEntries(
  APPROVED_SUPPORTER_COLORS.map((color) => [
    color.id,
    color,
  ])
);

const APPROVED_COLOR_BY_HEX = Object.fromEntries(
  APPROVED_SUPPORTER_COLORS.map((color) => [
    color.value.toLowerCase(),
    color,
  ])
);

const PRESET_BY_ID = Object.fromEntries(
  SUPPORTER_GRADIENT_PRESETS.map((preset) => [
    preset.id,
    preset,
  ])
);

const APPROVED_DIRECTION_VALUES = new Set(
  SUPPORTER_GRADIENT_DIRECTIONS.map(
    (direction) => direction.value
  )
);

export const canUseSupporterStyles = (entitled) => {
  return (
    Number(entitled) >= SUPPORTER_MIN_ENTITLEMENT
  );
};

export const canUseAnimatedSupporterStyles = (
  entitled
) => {
  return (
    Number(entitled) >=
    SUPPORTER_ANIMATION_ENTITLEMENT
  );
};

export const getApprovedColorById = (id) => {
  return APPROVED_COLOR_BY_ID[id] || null;
};

export const createCustomGradientToken = ({
  start,
  end,
  direction,
  animated,
}) => {
  return [
    'gradient',
    start,
    end,
    String(direction),
    animated ? 'motion' : 'static',
  ].join(':');
};

export const parseCustomGradientToken = (value) => {
  if (
    typeof value !== 'string' ||
    !value.toLowerCase().startsWith('gradient:')
  ) {
    return null;
  }

  const parts = value.toLowerCase().split(':');

  if (parts.length !== 5) {
    return null;
  }

  const [
    type,
    start,
    end,
    direction,
    motion,
  ] = parts;

  if (type !== 'gradient') {
    return null;
  }

  if (!APPROVED_COLOR_BY_ID[start]) {
    return null;
  }

  if (!APPROVED_COLOR_BY_ID[end]) {
    return null;
  }

  if (!APPROVED_DIRECTION_VALUES.has(direction)) {
    return null;
  }

  if (motion !== 'static' && motion !== 'motion') {
    return null;
  }

  return {
    type: 'gradient',
    id: value.toLowerCase(),
    label: 'Custom gradient',
    colorIds: [start, end, start],
    start,
    end,
    direction: Number(direction),
    animated: motion === 'motion',
  };
};

export const normalizeSupporterColor = (value) => {
  if (typeof value !== 'string') {
    return DEFAULT_SUPPORTER_COLOR;
  }

  const normalized = value.trim().toLowerCase();

  if (
    normalized === '' ||
    normalized === 'none' ||
    normalized === 'default' ||
    normalized === '#565656'
  ) {
    return DEFAULT_SUPPORTER_COLOR;
  }

  if (APPROVED_COLOR_BY_HEX[normalized]) {
    return normalized;
  }

  if (PRESET_BY_ID[normalized]) {
    return normalized;
  }

  if (parseCustomGradientToken(normalized)) {
    return normalized;
  }

  return DEFAULT_SUPPORTER_COLOR;
};

/*
 * Prevent a 300–599 user from retaining or submitting
 * an animated style through stale client state.
 */
export const coerceSupporterColorForEntitlement = (
  value,
  entitled
) => {
  const normalized = normalizeSupporterColor(value);

  if (
    canUseAnimatedSupporterStyles(entitled) ||
    !normalized
  ) {
    return normalized;
  }

  const customGradient =
    parseCustomGradientToken(normalized);

  if (customGradient) {
    return createCustomGradientToken({
      start: customGradient.start,
      end: customGradient.end,
      direction: customGradient.direction,
      animated: false,
    });
  }

  const preset = PRESET_BY_ID[normalized];

  if (preset) {
    const start = preset.colorIds[0];

    let end =
      preset.colorIds[preset.colorIds.length - 1];

    /*
     * Animated presets repeat their first color at
     * the end. Use the previous color for the static
     * downgraded version.
     */
    if (
      end === start &&
      preset.colorIds.length > 1
    ) {
      end =
        preset.colorIds[
          preset.colorIds.length - 2
        ];
    }

    return createCustomGradientToken({
      start,
      end,
      direction: preset.direction,
      animated: false,
    });
  }

  return normalized;
};

export const resolveSupporterStyle = (value) => {
  const normalized = normalizeSupporterColor(value);

  if (!normalized) {
    return {
      type: 'default',
      id: '',
      label: 'Default',
      animated: false,
    };
  }

  const solidColor = APPROVED_COLOR_BY_HEX[normalized];

  if (solidColor) {
    return {
      type: 'solid',
      id: solidColor.value,
      label: solidColor.label,
      value: solidColor.value,
      animated: false,
    };
  }

  const preset = PRESET_BY_ID[normalized];

  if (preset) {
    return {
      type: 'gradient',
      ...preset,
    };
  }

  const customGradient =
    parseCustomGradientToken(normalized);

  if (customGradient) {
    return customGradient;
  }

  return {
    type: 'default',
    id: '',
    label: 'Default',
    animated: false,
  };
};

export const getGradientBackground = (style) => {
  if (
    !style ||
    style.type !== 'gradient' ||
    !Array.isArray(style.colorIds)
  ) {
    return 'none';
  }

  const colors = style.colorIds
    .map(
      (colorId) =>
        APPROVED_COLOR_BY_ID[colorId]?.value
    )
    .filter(Boolean);

  if (colors.length < 2) {
    return 'none';
  }

  return `linear-gradient(
    ${style.direction || 120}deg,
    ${colors.join(', ')}
  )`;
};